import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/Stars";
import { Send, Check, X, ArrowLeft, ShieldCheck, Package, AlertTriangle } from "lucide-react";

export default function TradeRoom() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [showDispute, setShowDispute] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const scrollRef = useRef(null);
  const wsRef = useRef(null);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  const loadOffer = useCallback(async () => {
    try {
      const { data } = await api.get(`/offers/${offerId}`);
      setOffer(data);
      if (data.status === "accepted") {
        try { const rs = await api.get(`/reviews/offer/${offerId}`); setReviews(rs.data); } catch (e) {}
      }
    } catch (e) { toast.error("Cannot open trade"); navigate("/dashboard"); }
  }, [offerId, navigate]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/offers/${offerId}/messages`);
      setMessages(data);
    } catch (e) {}
  }, [offerId]);

  useEffect(() => { loadOffer(); }, [loadOffer]);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 2000);
    return () => clearInterval(t);
  }, [loadMessages]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return; // Google-auth users use polling
    let closed = false, attempts = 0, ws;
    const connect = () => {
      const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/chat/${offerId}?token=${token}`;
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { attempts = 0; };
      ws.onmessage = (e) => { try { addMessage(JSON.parse(e.data)); } catch (err) {} };
      ws.onclose = () => {
        wsRef.current = null;
        if (closed) return;
        attempts += 1;
        if (attempts <= 3) setTimeout(connect, Math.min(3000 * attempts, 12000));
      };
      ws.onerror = () => { try { ws.close(); } catch (err) {} };
    };
    connect();
    return () => { closed = true; try { ws && ws.close(); } catch (e) {} };
  }, [offerId, addMessage]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  if (!offer || !user) return <div className="py-20 text-center font-display font-extrabold text-xl animate-pulse">Loading trade…</div>;

  const isOwner = user.user_id === offer.to_user_id;
  const otherName = isOwner ? offer.from_user_name : offer.to_user_name;
  const otherId = isOwner ? offer.from_user_id : offer.to_user_id;
  const myReview = reviews.find((r) => r.rater_id === user.user_id);

  const loadReviews = async () => {
    try { const { data } = await api.get(`/reviews/offer/${offerId}`); setReviews(data); } catch (e) {}
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { offer_id: offerId, rating, comment });
      toast.success("Thanks for rating your trade partner!");
      loadReviews();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const raiseDispute = async () => {
    if (!disputeReason.trim()) { toast.error("Describe the problem"); return; }
    try {
      await api.post(`/offers/${offerId}/dispute`, { reason: disputeReason });
      toast.success("Dispute submitted — the House will review it");
      setShowDispute(false); setDisputeReason(""); loadOffer();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ text: t }));
      return;
    }
    try { await api.post(`/offers/${offerId}/messages`, { text: t }); loadMessages(); }
    catch (e) { toast.error("Message failed"); }
  };

  const act = async (action) => {
    try { await api.post(`/offers/${offerId}/${action}`); toast.success(`Offer ${action}ed`); loadOffer(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 font-bold text-sm mb-5 hover:text-[#B8860B]">
        <ArrowLeft size={16} strokeWidth={3} /> Dashboard
      </button>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: trade details */}
        <div className="space-y-5">
          <div className="bg-white nb-border rounded-2xl p-6 nb-shadow">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-[#F4F4F5] nb-border overflow-hidden flex items-center justify-center flex-shrink-0">
                {offer.listing_image ? <img src={fileUrl(offer.listing_image)} alt="" className="w-full h-full object-cover" /> : <Package className="text-black/20" />}
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-black/40">Trade for</div>
                <div className="font-display font-extrabold text-xl leading-tight">{offer.listing_title}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[#FBF7E9] nb-border rounded-lg p-3">
                <div className="text-[10px] font-bold uppercase text-black/50">Their offer</div>
                <div className="font-semibold">{offer.offer_description}</div>
                <div className="font-extrabold">${Number(offer.offered_value_usd).toLocaleString()}</div>
              </div>
              <div className="bg-[#FBF7E9] nb-border rounded-lg p-3">
                <div className="text-[10px] font-bold uppercase text-black/50">Item value</div>
                <div className="font-extrabold text-lg">${Number(offer.agreed_value).toLocaleString()}</div>
              </div>
            </div>

            {isOwner && offer.status === "pending" && (
              <div className="flex gap-2 mt-4">
                <button data-testid="tr-accept" onClick={() => act("accept")} className="flex-1 flex items-center justify-center gap-1 bg-[#E6B31E] text-black font-extrabold rounded-full py-3 border-2 border-black"><Check size={16} strokeWidth={3} /> Accept trade</button>
                <button data-testid="tr-reject" onClick={() => act("reject")} className="flex-1 flex items-center justify-center gap-1 bg-white text-black font-extrabold rounded-full py-3 border-2 border-black"><X size={16} strokeWidth={3} /> Reject</button>
              </div>
            )}
            {offer.status === "pending" && !isOwner && (
              <div className="mt-4 text-sm font-semibold text-black/50 bg-[#FBF7E9] nb-border rounded-lg p-3">Waiting for {otherName} to accept your offer.</div>
            )}
            {offer.status === "rejected" && <div className="mt-4 font-extrabold text-red-600">This offer was rejected.</div>}
          </div>

          {offer.status === "accepted" && (
            <div className="bg-black text-white rounded-2xl p-6 border-2 border-[#E6B31E]">
              {offer.trade_status === "disputed" ? (
                <div className="flex items-center gap-2 font-display font-extrabold text-xl text-red-400">
                  <AlertTriangle size={22} strokeWidth={2.5} /> Dispute under review by the House
                </div>
              ) : (
                <div className="flex items-center gap-2 font-display font-extrabold text-xl text-green-400">
                  <ShieldCheck size={22} strokeWidth={2.5} /> Trade agreed — no fees!
                </div>
              )}
              <p className="text-white/60 text-sm mt-2 font-medium">Both parties have agreed. Use the chat to arrange the swap of your items/services. Tradeable Exchange charges no commission.</p>

              <div className="mt-5 pt-5 border-t border-white/10">
                {myReview ? (
                  <div>
                    <div className="text-xs font-bold uppercase text-white/50 mb-1">Your rating</div>
                    <Stars value={myReview.rating} size={20} />
                  </div>
                ) : (
                  <div>
                    <div className="text-sm font-extrabold mb-2">Rate your trade with <Link to={`/user/${otherId}`} className="text-[#E6B31E] underline">{otherName}</Link></div>
                    <Stars value={rating} size={26} interactive onChange={setRating} />
                    <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                      placeholder="Leave a short review (optional)" className="w-full mt-3 p-3 rounded-xl bg-white text-black font-medium outline-none" />
                    <button data-testid="submit-review-btn" onClick={submitReview} className="w-full mt-2 bg-[#E6B31E] text-black font-extrabold rounded-full py-3 border-2 border-black">
                      Submit rating
                    </button>
                  </div>
                )}
              </div>

              {offer.trade_status !== "disputed" && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  {showDispute ? (
                    <div>
                      <textarea data-testid="dispute-reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} rows={2}
                        placeholder="What went wrong? The House will review this trade." className="w-full p-3 rounded-xl bg-white text-black font-medium outline-none" />
                      <div className="flex gap-2 mt-2">
                        <button data-testid="submit-dispute-btn" onClick={raiseDispute} className="flex-1 bg-red-500 text-white font-extrabold rounded-full py-2.5 border-2 border-black">Submit dispute</button>
                        <button onClick={() => setShowDispute(false)} className="px-4 bg-white text-black font-extrabold rounded-full py-2.5 border-2 border-black">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button data-testid="report-problem-btn" onClick={() => setShowDispute(true)} className="flex items-center gap-1.5 text-xs font-bold text-red-300 hover:text-red-200 transition-colors">
                      <AlertTriangle size={14} strokeWidth={3} /> Report a problem with this trade
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: chat */}
        <div className="bg-white nb-border rounded-2xl nb-shadow flex flex-col h-[70vh]">
          <div className="border-b-2 border-black px-5 py-3 font-display font-extrabold">Chat with <Link to={`/user/${otherId}`} className="text-[#B8860B] hover:underline">{otherName}</Link></div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && <div className="text-center text-black/40 font-semibold text-sm mt-8">Start the conversation to agree on terms.</div>}
            {messages.map((m) => {
              const mine = m.sender_id === user.user_id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2 border-2 border-black ${mine ? "bg-[#E6B31E] text-black" : "bg-white"}`}>
                    {!mine && <div className="text-[10px] font-bold text-black/50">{m.sender_name}</div>}
                    <div className="font-medium text-sm">{m.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t-2 border-black p-3 flex gap-2">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…" className="flex-1 p-3 nb-border rounded-xl font-medium outline-none focus:ring-2 focus:ring-[#E6B31E]" />
            <button data-testid="chat-send-btn" onClick={send} className="bg-black text-[#E6B31E] rounded-xl px-4 border-2 border-black"><Send size={18} strokeWidth={2.5} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
